/* eslint-disable @angular-eslint/no-input-rename */

/* eslint-disable @typescript-eslint/naming-convention */

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { Directionality } from '@angular/cdk/bidi';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    ConnectedPosition,
    FlexibleConnectedPositionStrategy,
    Overlay,
    OverlayConfig,
    OverlayRef,
    PositionStrategy,
    ScrollStrategy
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ViewportRuler } from '@angular/cdk/scrolling';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    ElementRef,
    forwardRef,
    NgZone,
    OnDestroy,
    ViewContainerRef,
    input,
    inject,
    DOCUMENT,
    effect
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { defer, fromEvent, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { delay, filter, map, switchMap, take, tap } from 'rxjs/operators';

import { ApAutocompleteOptionComponent, ApAutocompleteOptionSelectionChange } from '../option/option.component';

import { ApAutocompleteComponent } from './autocomplete-panel.component';

/**
 * Provider that allows the autocomplete to register as a ControlValueAccessor.
 *
 * @docs-private
 */
export const AP_AUTOCOMPLETE_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ApAutocompleteInputRefDirective),
    multi: true
};

/**
 * Creates an error to be thrown when attempting to use an autocomplete trigger without a panel.
 *
 * @docs-private
 */
export const getApAutocompleteMissingPanelError = (): Error =>
    Error(
        'Attempting to open an undefined instance of `ap-autocomplete`. ' +
            'Make sure that the id passed to the `apAutocomplete` is correct and that ' +
            `you're attempting to open it after the ngAfterContentInit hook.`
    );

@Directive({
    selector: 'input[apAutocompleteInputRef]',
    host: {
        class: 'ap-autocomplete-trigger',
        '[class.ap-autocomplete-above]': '_overlayPosition === "above"',
        '[class.ap-autocomplete-below]': '_overlayPosition === "below"',
        '[attr.role]': 'autocompleteDisabled() ? null : "combobox"',
        '[attr.aria-autocomplete]': 'autocompleteDisabled() ? null : "list"',
        '[attr.aria-activedescendant]': '(panelOpen && activeOption) ? activeOption.id() : null',
        '[attr.aria-expanded]': 'autocompleteDisabled() ? null : panelOpen.toString()',
        '[attr.aria-owns]': '(autocompleteDisabled() || !panelOpen) ? null : autocomplete()?.id',
        '[attr.aria-haspopup]': '!autocompleteDisabled()',
        // Note: we use `focusin`, as opposed to `focus`, in order to open the panel
        // a little earlier. This avoids issues where IE delays the focusing of the input.
        '(focus)': '_handleFocus()',
        '(blur)': '_onTouched()',
        '(input)': '_handleInput($event)',
        '(keydown)': '_handleKeydown($event)'
    },
    providers: [AP_AUTOCOMPLETE_VALUE_ACCESSOR]
})
export class ApAutocompleteInputRefDirective implements ControlValueAccessor, AfterViewInit, OnDestroy {
    private readonly _element = inject<ElementRef<HTMLInputElement>>(ElementRef);
    private readonly _overlay = inject(Overlay);
    private readonly _viewContainerRef = inject(ViewContainerRef);
    private readonly _zone = inject(NgZone);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _dir = inject(Directionality, { optional: true });
    private readonly _document = inject(DOCUMENT, { optional: true });
    private readonly _viewportRuler = inject(ViewportRuler);

    /**
     * Whether the autocomplete is disabled. When disabled, the element will
     * act as a regular input and the user won't be able to open the panel.
     */
    public readonly autocompleteDisabled = input(false, {
        alias: 'apAutocompleteDisabled',
        transform: (value: boolean): boolean => {
            return coerceBooleanProperty(value);
        }
    });

    /** Whether or not the autocomplete panel is open. */
    get panelOpen(): boolean {
        return this._overlayAttached && this.autocomplete().showPanel;
    }

    /**
     * A stream of actions that should close the autocomplete panel, including
     * when an option is selected, on blur, and when TAB is pressed.
     */
    get panelClosingActions(): Observable<ApAutocompleteOptionSelectionChange | null> {
        return merge(
            this.optionSelections,
            this.autocomplete()._keyManager.tabOut.pipe(filter(() => this._overlayAttached)),
            this.closeKeyEventStream,
            this._getOutsideClickStream(),
            this._overlayRef ? this._overlayRef.detachments().pipe(filter(() => this._overlayAttached)) : of()
        ).pipe(
            // Normalize the output so we return a consistent type.
            map((event) => (event instanceof ApAutocompleteOptionSelectionChange ? event : null))
        );
    }

    /** The currently active option, coerced to ApAutocompleteOptionComponent type. */
    get activeOption(): ApAutocompleteOptionComponent | null {
        const autocomplete = this.autocomplete();
        if (autocomplete && autocomplete._keyManager) {
            return autocomplete._keyManager.activeItem;
        }

        return null;
    }

    /** The autocomplete panel to be attached to this trigger. */
    readonly autocomplete = input<ApAutocompleteComponent>(undefined, { alias: 'apAutocomplete' });

    /** Element to connect to. */
    readonly connectedElement = input<ElementRef<HTMLElement>>(undefined);

    /**
     * Position of the autocomplete panel relative to the trigger element. A position of `auto`
     * will render the panel underneath the trigger if there is enough space for it to fit in
     * the viewport, otherwise the panel will be shown above it. If the position is set to
     * `above` or `below`, the panel will always be shown above or below the trigger. no matter
     * whether it fits completely in the viewport.
     */
    readonly position = input<'auto' | 'above' | 'below'>('auto', { alias: 'apAutocompletePosition' });

    /** Stream of autocomplete option selections. */
    readonly optionSelections: Observable<ApAutocompleteOptionSelectionChange> = defer(() => {
        const autocomplete = this.autocomplete();
        if (autocomplete && autocomplete.options) {
            return merge(...autocomplete.options.map((option) => option.selectionChange));
        }

        // If there are any subscribers before `ngAfterViewInit`, the `autocomplete` will be undefined.
        // Return a stream that we'll replace with the real one once everything is in place.
        return this._zone.onStable.asObservable().pipe(
            take(1),
            switchMap(() => this.optionSelections)
        );
    });
    private _overlayRef: OverlayRef | null;
    private _portal: TemplatePortal;
    private _componentDestroyed = false;
    private readonly _scrollStrategy: () => ScrollStrategy;
    protected _overlayPosition: 'above' | 'below' = 'below';

    /** Old value of the native input. Used to work around issues with the `input` event on IE. */
    private _previousValue: string | number | null;

    /** Strategy that is used to position the panel. */
    private _positionStrategy: FlexibleConnectedPositionStrategy;

    /** Whether or not the label state is being overridden. */
    private _manuallyFloatingLabel = false;

    /** The subscription for closing actions (some are bound to document). */
    private _closingActionsSubscription: Subscription;

    /** Subscription to viewport size changes. */
    private _viewportSubscription = Subscription.EMPTY;

    /**
     * Whether the autocomplete can open the next time it is focused. Used to prevent a focused,
     * closed autocomplete from being reopened if the user switches to another browser tab and then
     * comes back.
     */
    private _canOpenOnNextFocus = true;

    /** Whether the element is inside of a ShadowRoot component. */
    private readonly _isInsideShadowRoot = false;

    /** Stream of keyboard events that can close the panel. */
    private readonly closeKeyEventStream = new Subject<void>();

    private _overlayAttached = false;
    private _wrapElement;

    constructor() {
        const _overlay = this._overlay;

        this._scrollStrategy = () => _overlay.scrollStrategies.reposition();

        effect(() => {
            if (this.position() && this._positionStrategy) {
                this._setStrategyPositions(this._positionStrategy);

                if (this.panelOpen) {
                    this._overlayRef?.updatePosition();
                }
            }
        });
    }

    /** `View -> model callback called when value changes` */
    public _onChange: (value: any) => void = () => {};

    /** `View -> model callback called when autocomplete has been touched` */
    public _onTouched = () => {};

    public ngAfterViewInit(): void {
        if (typeof window !== 'undefined') {
            this._zone.runOutsideAngular(() => {
                window.addEventListener('blur', this._windowBlurHandler);
            });
        }
    }

    public ngOnDestroy(): void {
        if (typeof window !== 'undefined') {
            window.removeEventListener('blur', this._windowBlurHandler);
        }

        this._viewportSubscription.unsubscribe();
        this._componentDestroyed = true;
        this._destroyPanel();
        this.closeKeyEventStream.complete();
    }

    public openPanel(): void {
        this._attachOverlay();
    }

    public closePanel(): void {
        this._resetLabel();

        if (!this._overlayAttached) {
            return;
        }

        this.autocomplete()._isOpen = false;
        this._overlayAttached = false;

        if (this._overlayRef && this._overlayRef.hasAttached()) {
            this._overlayRef.detach();
            this._closingActionsSubscription.unsubscribe();
        }

        // Note that in some cases this can end up being called after the component is destroyed.
        // Add a check to ensure that we don't try to run change detection on a destroyed view.
        if (!this._componentDestroyed) {
            // We need to trigger change detection manually, because
            // `fromEvent` doesn't seem to do it at the proper time.
            // This ensures that the label is reset when the
            // user clicks outside.
            this._changeDetectorRef.detectChanges();
        }
    }

    public updatePosition(): void {
        if (this._overlayAttached) {
            this._overlayRef?.updatePosition();
        }
    }

    public clearValue(): void {
        this.writeValue('');
        this._onChange('');
    }

    // Implemented as part of ControlValueAccessor.
    public writeValue(value: any): void {
        Promise.resolve(null).then(() => this._setTriggerValue(value));
    }

    // Implemented as part of ControlValueAccessor.
    public registerOnChange(fn: (value: any) => unknown): void {
        this._onChange = fn;
    }

    // Implemented as part of ControlValueAccessor.
    public registerOnTouched(fn: () => unknown): void {
        this._onTouched = fn;
    }

    // Implemented as part of ControlValueAccessor.
    public setDisabledState(isDisabled: boolean): void {
        this._element.nativeElement.disabled = isDisabled;
    }

    public _handleKeydown(event: KeyboardEvent): void {
        const code = event.code;

        const autocomplete = this.autocomplete();
        if (this.activeOption && code === 'Enter' && this.panelOpen) {
            this.activeOption.selectViaInteraction();
            this._resetActiveItem();
            event.preventDefault();
        } else if (autocomplete) {
            const prevActiveItem = autocomplete._keyManager.activeItem;
            const isArrowKey = code === 'ArrowUp' || code === 'ArrowDown';

            if (this.panelOpen || code === 'Tab') {
                autocomplete._keyManager.onKeydown(event);
            } else if (isArrowKey && this._canOpen()) {
                this.openPanel();
            }

            if (isArrowKey || autocomplete._keyManager.activeItem !== prevActiveItem) {
                this._scrollToOption();
            }
        }
    }

    public _handleInput(event: any): void {
        const target = event.target as HTMLInputElement;
        let value: number | string | null = target.value;

        // Based on `NumberValueAccessor` from forms.
        if (target.type === 'number') {
            value = value === '' ? null : parseFloat(value);
        }

        // If the input has a placeholder, IE will fire the `input` event on page load,
        // focus and blur, in addition to when the user actually changed the value. To
        // filter out all of the extra events, we save the value on focus and between
        // `input` events, and we check whether it changed.
        // See: https://connect.microsoft.com/IE/feedback/details/885747/
        if (this._previousValue !== value) {
            this._previousValue = value;
            this._onChange(value);
            if (this._canOpen() && this._document.activeElement === event.target) {
                this.openPanel();
            }
        }
    }

    public _handleFocus(): void {
        if (!this._canOpenOnNextFocus) {
            this._canOpenOnNextFocus = true;
            this.blur();
        } else if (this._canOpen()) {
            this._previousValue = this._element.nativeElement.value;
            this.openPanel();
        }
    }

    /**
     * Event handler for when the window is blurred. Needs to be an
     * arrow function in order to preserve the context.
     */
    private readonly _windowBlurHandler = () => {
        // If the user blurred the window while the autocomplete is focused, it means that it'll be
        // refocused when they come back. In this case we want to skip the first focus event, if the
        // pane was closed, in order to avoid reopening it unintentionally.
        this._canOpenOnNextFocus = this._document.activeElement !== this._element.nativeElement || this.panelOpen;
    };

    /** Stream of clicks outside of the autocomplete panel. */
    private _getOutsideClickStream(): Observable<any> {
        return merge(fromEvent(this._document, 'click'), fromEvent(this._document, 'touchend')).pipe(
            filter((event: UIEvent) => {
                // If we're in the Shadow DOM, the event target will be the shadow root, so we have to
                // fall back to check the first element in the path of the click event.
                const clickTarget = (
                    this._isInsideShadowRoot && event.composedPath ? event.composedPath()[0] : event.target
                ) as HTMLElement;

                return (
                    this._overlayAttached &&
                    clickTarget !== this._element.nativeElement &&
                    !!this._overlayRef &&
                    !this._overlayRef.overlayElement.contains(clickTarget)
                );
            })
        );
    }

    /** If the label has been manually elevated, return it to its normal state. */
    private _resetLabel(): void {
        if (this._manuallyFloatingLabel) {
            this._manuallyFloatingLabel = false;
        }
    }

    /**
     * Given that we are not actually focusing active options, we must manually adjust scroll
     * to reveal options below the fold. First, we find the offset of the option from the top
     * of the panel. If that offset is below the fold, the new scrollTop will be the offset -
     * the panel height + the option height, so the active option will be just visible at the
     * bottom of the panel. If that offset is above the top of the visible panel, the new scrollTop
     * will become the offset. If that offset is visible within the panel already, the scrollTop is
     * not adjusted.
     */
    private _scrollToOption(): void {
        const index = this.autocomplete()._keyManager.activeItemIndex || 0;
        const labelCount = 0;

        if (index === 0) {
            // If we've got one group label before the option and we're at the top option,
            // scroll the list to the top. This is better UX than scrolling the list to the
            // top of the option, because it allows the user to read the top group's label.
            this.autocomplete()._setScrollTop(0);
        } else {
            this.autocomplete()._determineScrollTop(index + labelCount);
        }
    }

    /**
     * This method listens to a stream of panel closing actions and resets the
     * stream every time the option list changes.
     */
    private _subscribeToClosingActions(): Subscription {
        const optionChanges = this.autocomplete().options.changes.pipe(
            tap(() => this._positionStrategy.reapplyLastPosition()),
            // Defer emitting to the stream until the next tick, because changing
            // bindings in here will cause "changed after checked" errors.
            delay(0)
        );

        // When the zone is stable initially, and when the option list changes...
        return (
            merge(this.panelClosingActions, optionChanges)
                .pipe(
                    tap(() => {
                        this._resetActiveItem();
                        this.autocomplete()._setVisibility();

                        if (this.panelOpen) {
                            this._overlayRef?.updatePosition();
                        }
                    }),
                    // when the first closing event occurs...
                    take(1)
                )
                // set the value, close the panel, and complete.
                .subscribe((event) => this._setValueAndClose(event))
        );
    }

    /** Destroys the autocomplete suggestion panel. */
    private _destroyPanel(): void {
        if (this._overlayRef) {
            this.closePanel();
            this._overlayRef.dispose();
            this._wrapElement.remove();
            this._wrapElement = null;
            this._overlayRef = null;
        }
    }

    private _setTriggerValue(value: any): void {
        const toDisplay = value;

        // Simply falling back to an empty string if the display value is falsy does not work properly.
        // The display value can also be the number zero and shouldn't fall back to an empty string.
        const inputValue = toDisplay != null ? toDisplay : '';

        // If it's used within a `MatFormField`, we should set it through the property so it can go
        // through change detection.
        this._element.nativeElement.value = inputValue;

        this._previousValue = inputValue;
    }

    /**
     * This method closes the panel, and if a value is specified, also sets the associated
     * control to that value. It will also mark the control as dirty if this interaction
     * stemmed from the user.
     */
    private _setValueAndClose(event: ApAutocompleteOptionSelectionChange | null): void {
        if (event && event.source) {
            this._clearPreviousSelectedOption(event.source);
            // this._setTriggerValue(event.source.value);
            this._onChange(event.source.value());
            this._element.nativeElement.focus();
            this.autocomplete()._emitSelectEvent(event.source);
        }

        this.closePanel();
    }

    /**
     * Clear any previous selected option and emit a selection change event for this option
     */
    private _clearPreviousSelectedOption(skip: ApAutocompleteOptionComponent): void {
        this.autocomplete().options.forEach((option) => {
            if (option !== skip && option.selected()) {
                option.deselect();
            }
        });
    }

    /**
     * Render the cdk overlay as close as possible to the clicked element.
     * This will prevent style bleeds with (.ap) on retail which should be rendered inside PEGA
     *
     * @private
     */
    private renderNextToConnectedElement(): void {
        if (!this._wrapElement) {
            this._wrapElement = this._document.createElement('div');
            this._wrapElement.className = 'ap'; //always add ap class
            this.connectedElement().nativeElement.appendChild(this._wrapElement);
        }
        this._wrapElement.appendChild(this._overlayRef.hostElement.parentElement);
    }

    private _attachOverlay(): void {
        const autocomplete = this.autocomplete();
        if (!autocomplete) {
            throw getApAutocompleteMissingPanelError();
        }

        let overlayRef = this._overlayRef;

        if (!overlayRef) {
            this._portal = new TemplatePortal(autocomplete.template, this._viewContainerRef);
            overlayRef = this._overlay.create(this._getOverlayConfig());
            this._overlayRef = overlayRef;
            this.renderNextToConnectedElement();

            // Use the `keydownEvents` in order to take advantage of
            // the overlay event targeting provided by the CDK overlay.
            overlayRef.keydownEvents().subscribe((event) => {
                // Close when pressing ALT + UP_ARROW, based on the a11y guidelines.
                // See: https://www.w3.org/TR/wai-aria-practices-1.1/#textbox-keyboard-interaction
                // For beter user experience close the autocomplete when user press Escape
                if (event.code === 'ArrowUp' && event.altKey) {
                    this._resetActiveItem();
                    this.closeKeyEventStream.next();

                    // We need to stop propagation, otherwise the event will eventually
                    // reach the input itself and cause the overlay to be reopened.
                    event.stopPropagation();
                    event.preventDefault();
                    this.blur();
                } else if (event.code === 'Escape') {
                    this.closeKeyEventStream.next();
                }
            });

            if (this._viewportRuler) {
                this._viewportSubscription = this._viewportRuler.change().subscribe(() => {
                    if (this.panelOpen && overlayRef) {
                        overlayRef.updateSize({ width: this._getPanelWidth() });
                    }
                });
            }
        } else {
            // Update the trigger, panel width and direction, in case anything has changed.
            this._positionStrategy.setOrigin(this.connectedElement());
            overlayRef.updateSize({ width: this._getPanelWidth() });
        }

        if (overlayRef && !overlayRef.hasAttached()) {
            overlayRef.attach(this._portal);
            this._closingActionsSubscription = this._subscribeToClosingActions();
        }

        autocomplete._setVisibility();
        autocomplete._isOpen = true;
        this._overlayAttached = true;
    }

    private _getOverlayConfig(): OverlayConfig {
        return new OverlayConfig({
            positionStrategy: this._getOverlayPosition(),
            scrollStrategy: this._scrollStrategy(),
            width: this._getPanelWidth(),
            direction: this._dir
        });
    }

    private _getOverlayPosition(): PositionStrategy {
        const strategy = this._overlay
            .position()
            .flexibleConnectedTo(this.connectedElement())
            .withFlexibleDimensions(false)
            .withPush(false);

        this._setStrategyPositions(strategy);
        this._positionStrategy = strategy;
        this._positionStrategy.positionChanges.subscribe((positionChange) => {
            this._overlayPosition = positionChange.connectionPair.panelClass === 'ap-autocomplete-panel-above' ? 'above' : 'below';
            this._changeDetectorRef.markForCheck();
        });
        return strategy;
    }

    /** Sets the positions on a position strategy based on the directive's input state. */
    private _setStrategyPositions(positionStrategy: FlexibleConnectedPositionStrategy): void {
        const belowPosition: ConnectedPosition = {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
        };
        const abovePosition: ConnectedPosition = {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',

            // The overlay edge connected to the trigger should have squared corners, while
            // the opposite end has rounded corners. We apply a CSS class to swap the
            // border-radius based on the overlay position.
            panelClass: 'ap-autocomplete-panel-above'
        };

        let positions: ConnectedPosition[];

        const position = this.position();
        if (position === 'above') {
            positions = [abovePosition];
        } else if (position === 'below') {
            positions = [belowPosition];
        } else {
            positions = [belowPosition, abovePosition];
        }

        positionStrategy.withPositions(positions);
    }

    private _getPanelWidth(): number | string {
        return this.autocomplete().panelWidth() || this._getHostWidth();
    }

    /** Returns the width of the input element, so the panel width can match it. */
    private _getHostWidth(): number {
        return this.connectedElement().nativeElement.getBoundingClientRect().width;
    }

    /**
     * Resets the active item to -1 so arrow events will activate the
     * correct options, or to 0 if the consumer opted into it.
     */
    private _resetActiveItem(): void {
        this.autocomplete()._keyManager.setActiveItem(this.autocomplete().autoActiveFirstOption ? 0 : -1);
    }

    /** Determines whether the panel can be opened. */
    private _canOpen(): boolean {
        const element = this._element.nativeElement;
        return !element.readOnly && !element.disabled && !this.autocompleteDisabled();
    }

    private blur(): void {
        this._element.nativeElement.blur();
    }
}
